import type { PrefabData } from "./prefabData";
import { MetalBlock } from "../../things/metalBlock.ts";
import { BossPlowRobot } from "../../mobs/bossPlowRobot.ts";
import { BlastDoor } from "../../things/blastDoor.ts";
import { WarningFloor } from "../../things/floors/warningFloor.ts";
import { SlidingDoor } from "../../things/slidingDoor.ts";
import { Direction } from "../../direction.ts";
import { JetpackLootBox } from "../../things/jetpackLootBox.ts";
import { GrayStoneFloor1 } from "../../things/floors/grayStoneFloor1.ts";
import { GraySlab } from "../../things/floors/graySlab.ts";
import { VendingMachine } from "../../things/vendingMachine.ts";

export const desksData: PrefabData = {
	legend: {
		D: "DeskWithComputer",
		O: "OfficeChair",
	},
	layout: ["OOOO", "DDDD", "DDDD", "OOOO"],
};

export const plantsCornerData: PrefabData = {
	cornerRequired: true,
	legend: {
		P: "PottedPlant",
	},
	layout: ["PP", "P "],
};

export const borderData: PrefabData = {
	borderRequired: true,
	legend: {
		D: "DeskWithComputer",
		A: "DeskWithMachine",
		M: "MachineRack",
	},
	layout: ["MMADMM"],
};

export const machinesDoubleRowAtBorderData: PrefabData = {
	borderRequired: true,
	legend: {
		M: "MachineRack",
	},
	layout: [
		"MMMM",
		"MMMM",
	],
};

export const machinesAtBorderData: PrefabData = {
	borderRequired: true,
	legend: {
		M: "MachineRack",
	},
	layout: [
		"MMMM",
	],
};

export const miniBossRobotRoom: PrefabData = {
	borderRequired: false,
	legend: {
		"#": MetalBlock.key,
		"+": { class: SlidingDoor.key, props: { direction: Direction.W, group: "powerplantminiboss" } },
		"X": { class: BossPlowRobot.key, props: { state: "OFF" } },
		".": WarningFloor.key,
	},
	layout: [
		"###",
		"#X#",
		"#+#",
		"...",
	],
};

export const powerPlantLootRoom: PrefabData = {
	borderRequired: false,
	legend: {
		"#": MetalBlock.key,
		"+": { class: SlidingDoor.key, props: { direction: Direction.E, group: "powerplantminiboss" } },
		"X": { class: JetpackLootBox.key },
		".": GraySlab.key,
	},
	layers: [
		[
			" ...",
			"....",
			"....",
			"....",
			"    ",
		],
		[
			" ...",
			"##+#",
			"#XX#",
			"#XX#",
			"####",
		]
	]
};

export const deskData: PrefabData = {
	legend: {
		W: "WoodenDesk",
	},
	layout: ["W"],
};

export const woodenDesksData: PrefabData = {
	borderRequired: true,
	legend: {
		O: "OfficeChair",
		W: "WoodenCabinet",
		A: "WoodenDesk",
	},
	layout: ["WAAW", " OO "],
};

export const shelvesData: PrefabData = {
	borderRequired: true,
	legend: {
		W: "WoodenShelf",
	},
	layout: ["WWWW"],
};

export const computerDesksData: PrefabData = {
	borderRequired: true,
	legend: {
		D: "DeskWithComputer",
		O: "OfficeChair",
	},
	layout: ["DD", "OO"],
};

export const singleComputerDeskData: PrefabData = {
	borderRequired: true,
	legend: {
		D: "DeskWithComputer",
		O: "OfficeChair",
	},
	layout: ["D", "O"],
};

export const barrelsOnWallData: PrefabData = {
	borderRequired: true,
	legend: {
		E: "EmptyBarrel",
	},
	layout: ["EEEEE"],
};

export const explosiveBarrelsOnWallData: PrefabData = {
	borderRequired: true,
	legend: {
		E: "ExplosiveBarrel",
	},
	layout: ["EEEEE"],
};

export const explosiveBarrelsData: PrefabData = {
	legend: {
		E: "ExplosiveBarrel",
	},
	layout: ["EEE", "EEE"],
};

export const machinesWithWarningStripesData: PrefabData = {
	borderRequired: true,
	legend: {
		D: "DeskWithComputer",
		A: "DeskWithMachine",
		M: "MachineRack",
		".": "WarningFloor",
	},
	layout: [
		".MDAMM.",
		"......."
	],
};

export const offlineRobotsData: PrefabData = {
	borderRequired: true,
	legend: {
		".": "GraySlab",
		R: { class: "Robot", props: { state: "OFF" } },
	},
	layers: [
		["...."],
		["RRRR"]
	],
};

export const offlineRobots2Data: PrefabData = {
	borderRequired: true,
	legend: {
		".": "GraySlab",
		"#": "MetalBlock",
		R: { class: "Robot", props: { state: "OFF" } },
	},
	layers: [
		[" .... "],
		["#RRRR#"]
	],
};

export const offlineRobots3Data: PrefabData = {
	borderRequired: true,
	legend: {
		".": "GraySlab",
		R: { class: "Robot", props: { state: "OFF", lookDirection: Direction.E } },
	},
	layers: [
		[".."],
		["RR"]
	],
};

export const fenceWithPlantsData: PrefabData = {
	legend: {
		".": "BrownFloor",
		D: "DeadPlant",
		"=": "Fence",
		A: "Plant",
	},
	layers: [
		["        ", " ...... ", " ...... ", " ...... ", " ...... ", "        "],
		["========", "= D   A=", "=ADADA =", "=D  A  =", "= A  DD=", "========"],
	],
};

export const fencedLootBoxesData: PrefabData = {
	legend: {
		"=": "Fence",
		".": "GrayStoneFloor1",
		"#": "MetalBlock",
		M: "MetalLootBox",
	},
	layers: [
		["    ", " .. ", " .. ", "    "],
		["#==#", "=MM=", "=MM=", "#==#"],
	],
};

export const metalCornerData: PrefabData = {
	cornerRequired: true,
	legend: {
		W: "MetalBlock",
	},
	layout: ["WW", "W "],
};

export const cornerPrisonCellData: PrefabData = {
	cornerRequired: true,
	legend: {
		"=": "Fence",
		W: "MetalBlock",
		"/": "MetalDoor",
		".": "WarningFloor",
		D: "WoodenDesk",
	},
	layout: [" D W", "   W", "   W", "=/=W", "...."],
};

export const fenceBoxesData: PrefabData = {
	borderRequired: true,
	legend: {
		"=": "Fence",
		$: "LootBox",
		W: "MetalBlock",
	},
	layout: ["W$$$$W", "W====W"],
};

export const pillarsData: PrefabData = {
	legend: {
		W: "MetalBlock",
	},
	layout: ["WW", "WW"],
};

export const bigContainerData: PrefabData = {
	borderRequired: true,
	legend: {
		B: "BigContainer",
	},
	layout: ["B"],
};

export const vendingMachineData: PrefabData = {
	borderRequired: true,
	legend: {
		V: VendingMachine.key,
	},
	layout: ["V"],
};
